import type { RefObject } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, LayoutGrid, Plus, RefreshCw, Trash2 } from "lucide-react";
import {
  Button,
  DataTable,
  FormDrawer,
  TableIdentity,
  type DataTableColumn,
  type DataTableState,
} from "@h2j-data-team/ui";

import type { GroupApp } from "@repo/shared";
import { toast } from "@/components/shared";
import { getErrorMessage } from "@/lib/api-client";
import { tableMessages } from "@/lib/ui-labels";
import { useTableDensity } from "@/lib/use-table-density";
import { useRefreshAppMetadata } from "@/features/qlik-connections/api";

function formatRelativeTime(
  dateString: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60_000);
  if (diffMinutes < 1) return t("relativeTime.justNow");
  if (diffMinutes < 60) return t("relativeTime.minutesAgo", { count: diffMinutes });
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return t("relativeTime.hoursAgo", { count: diffHours });
  const diffDays = Math.floor(diffHours / 24);
  return t("relativeTime.daysAgo", { count: diffDays });
}

function GroupAppActions({ app, onRemove }: { app: GroupApp; onRemove: (app: GroupApp) => void }) {
  const { t } = useTranslation("sync-groups");
  const refreshMetadata = useRefreshAppMetadata();

  const handleRefresh = () => {
    refreshMetadata.mutate(
      { connectionId: app.connectionId, appId: app.appId },
      {
        onSuccess: () => toast.success(t("apps.metadataCacheUpdated")),
        onError: (err) => toast.error(getErrorMessage(err, t("apps.failedUpdateMetadata"))),
      },
    );
  };

  return (
    <div className="flex items-center justify-end gap-1">
      <Button
        size="icon-sm"
        variant="ghost"
        onClick={handleRefresh}
        loading={refreshMetadata.isPending}
        aria-label={t("apps.refreshMetadataFor", { name: app.appName })}
        title={t("apps.refreshMetadata")}
      >
        <RefreshCw />
      </Button>
      <Button
        size="icon-sm"
        variant="ghost"
        onClick={() => onRemove(app)}
        aria-label={t("apps.removeApp", { name: app.appName })}
        title={t("apps.removeApp", { name: app.appName })}
      >
        <Trash2 />
      </Button>
    </div>
  );
}

// A group has a handful of apps, so the table shows them all on one page and never changes state.
const NO_STATE_CHANGE = () => {};

export function ManageAppsDrawer({
  open,
  onOpenChange,
  apps,
  onRemove,
  onAddApps,
  restoreFocusRef,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  apps: GroupApp[];
  onRemove: (app: GroupApp) => void;
  onAddApps: () => void;
  /** The control that opened the drawer: focus goes back to it on close. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation("sync-groups");
  const { t: tc } = useTranslation("common");
  const { density } = useTableDensity();

  const columns: DataTableColumn<GroupApp>[] = [
    {
      id: "app",
      header: t("apps.colApp"),
      rowHeader: true,
      variant: "identity",
      cell: (app) => (
        <TableIdentity
          leading={<LayoutGrid />}
          name={
            app.connectionBaseUrl ? (
              <a
                href={`${app.connectionBaseUrl.replace(/\/$/, "")}/sense/app/${app.appId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex max-w-full items-center gap-1 hover:underline"
              >
                <span className="truncate">{app.appName}</span>
                <ArrowUpRight className="size-3 shrink-0" aria-hidden />
              </a>
            ) : (
              app.appName
            )
          }
          detail={
            (app.connectionName ?? t("apps.unknownConnection")) +
            (app.spaceName ? ` · ${app.spaceName}` : "") +
            (app.ownerName ? ` · ${app.ownerName}` : "")
          }
          detailMono={false}
        />
      ),
    },
    {
      id: "metadata",
      header: t("apps.colMetadata"),
      width: 110,
      cell: (app) =>
        app.metadataFetchedAt ? (
          <span
            title={t("apps.metadataUpdated", {
              time: formatRelativeTime(app.metadataFetchedAt, t),
            })}
          >
            {formatRelativeTime(app.metadataFetchedAt, t)}
          </span>
        ) : (
          <span className="text-faint">{t("apps.metadataNever")}</span>
        ),
    },
  ];

  const state: DataTableState = {
    page: 1,
    pageSize: Math.max(apps.length, 1),
    query: "",
    filters: {},
    selection: [],
  };

  return (
    <FormDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      closeLabel={tc("close")}
      title={t("apps.sectionTitle")}
      description={t("apps.sectionDescription")}
      restoreFocusRef={restoreFocusRef}
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("apps.done")}
          </Button>
          <Button type="button" onClick={onAddApps}>
            <Plus />
            {t("apps.addApps")}
          </Button>
        </>
      }
    >
      <DataTable
        label={t("apps.sectionTitle")}
        density={density}
        columns={columns}
        rows={apps}
        getRowId={(app) => app.id}
        state={state}
        onStateChange={NO_STATE_CHANGE}
        rowActions={{
          label: t("apps.colActions"),
          width: 88,
          render: (app) => <GroupAppActions app={app} onRemove={onRemove} />,
        }}
        messages={tableMessages({
          empty: {
            icon: <LayoutGrid />,
            title: t("apps.noAppsTitle"),
            description: t("apps.noAppsDescription"),
          },
        })}
      />
    </FormDrawer>
  );
}
